import patchStateReducer from '@coxy/utils/src/redux/patch-state'
// Состояние бэкенда: списки и флаги загрузки. Запросы живут в actions, подписки на оплату — в middleware.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { actionUserLogout } from '@shared-core/features/auth/actions/global'
import {
  type CardIssueState,
  type ModelBackendBank,
  type ModelBackendCard,
  type ModelBackendTransaction,
  type ModelCardIssueInvoice,
  type ModelCardTemplate,
  type ModelUserMe,
  modelCard,
} from '@shared-core/features/backend/models'

export const storeKey = '@redux/backend'

const TERMINAL_ISSUE_STATES: ModelCardIssueInvoice['state'][] = ['completed', 'failed', 'expired']

export type CardBlockPreparation = { idempotency: string; channel: string; expiresAt: number }

export type BackendState = {
  emailPromptShown: boolean
  cardBlockPreparations: Record<string, CardBlockPreparation>
  cardBlockPreparing: Record<string, boolean>
  cardIssue: CardIssueState
  pendingCardIssueInvoices: Record<string, ModelCardIssueInvoice>
  pendingCardIssueInvoice: ModelCardIssueInvoice | null
  isCardTopupLoading: boolean
  cardIssueReadyTxId: string | null
  cardIssueShownTxIds: string[]
  cardIssueKnownActiveIds: Record<string, string[]>
  banks: ModelBackendBank[] | null
  isBanksLoading: boolean
  banksError: string | null
  cards: ModelBackendCard[] | null
  isCardsLoading: boolean
  cardsError: string | null
  cardTemplates: ModelCardTemplate[] | null
  isCardTemplatesLoading: boolean
  cardTemplatesError: string | null
  transactions: ModelBackendTransaction[] | null
  isTransactionsLoading: boolean
  transactionsError: string | null
  user: ModelUserMe | null
  isUserLoading: boolean
  userError: string | null
}

const initialState: BackendState = {
  emailPromptShown: false,
  cardBlockPreparations: {},
  cardBlockPreparing: {},
  cardIssue: { invoice: null, isLoading: false, error: null },
  pendingCardIssueInvoices: {},
  pendingCardIssueInvoice: null,
  isCardTopupLoading: false,
  cardIssueReadyTxId: null,
  cardIssueShownTxIds: [],
  cardIssueKnownActiveIds: {},
  banks: null,
  isBanksLoading: false,
  banksError: null,
  cards: null,
  isCardsLoading: false,
  cardsError: null,
  cardTemplates: null,
  isCardTemplatesLoading: false,
  cardTemplatesError: null,
  transactions: null,
  isTransactionsLoading: false,
  transactionsError: null,
  user: null,
  isUserLoading: false,
  userError: null,
}

export const whitelist: Array<keyof BackendState> = [
  'cardBlockPreparations',
  'pendingCardIssueInvoice',
  'pendingCardIssueInvoices',
  'cardIssueShownTxIds',
  'cardIssueKnownActiveIds',
]

export const backendSlice = createSlice({
  name: storeKey,
  initialState,
  reducers: {
    emailPromptShown(state) {
      state.emailPromptShown = true
    },
    cardBlockPreparationCleared(state, action: PayloadAction<string>) {
      delete state.cardBlockPreparations[action.payload]
    },
    cardBlockPreparingChanged(state, action: PayloadAction<{ cardId: string; isPreparing: boolean }>) {
      if (action.payload.isPreparing) {
        state.cardBlockPreparing[action.payload.cardId] = true
        return
      }

      delete state.cardBlockPreparing[action.payload.cardId]
    },
    cardBlockPreparationSaved(state, action: PayloadAction<{ cardId: string; preparation: CardBlockPreparation }>) {
      state.cardBlockPreparations[action.payload.cardId] = action.payload.preparation
      delete state.cardBlockPreparing[action.payload.cardId]
    },
    cardUpdated(state, action: PayloadAction<{ id: string; status: string; balance?: string | null }>) {
      if (!Array.isArray(state.cards)) return
      const status = modelCard.shape.status.safeParse(action.payload.status)

      if (!status.success) return
      const card = state.cards.find((item) => item.id === action.payload.id)

      if (!card) return
      card.status = status.data

      if (typeof action.payload.balance === 'string') card.balance = action.payload.balance
    },
    issueStateChanged(state, action: PayloadAction<{ txId: string; state: ModelCardIssueInvoice['state'] }>) {
      if (state.cardIssue.invoice?.txId === action.payload.txId) state.cardIssue.invoice.state = action.payload.state
      const invoice = state.pendingCardIssueInvoices?.[action.payload.txId]

      if (invoice) invoice.state = action.payload.state

      if (TERMINAL_ISSUE_STATES.includes(action.payload.state)) {
        if (state.pendingCardIssueInvoices) delete state.pendingCardIssueInvoices[action.payload.txId]

        if (state.pendingCardIssueInvoice?.txId === action.payload.txId) state.pendingCardIssueInvoice = null
      }
    },
    cardIssueChanged(state, action: PayloadAction<CardIssueState>) {
      state.pendingCardIssueInvoices ??= {}

      if (state.pendingCardIssueInvoice)
        state.pendingCardIssueInvoices[state.pendingCardIssueInvoice.txId] = state.pendingCardIssueInvoice
      state.cardIssue = action.payload
      state.pendingCardIssueInvoice = action.payload.invoice

      if (action.payload.invoice) {
        state.pendingCardIssueInvoices[action.payload.invoice.txId] = action.payload.invoice
      }
    },
    patchBackendState(state, action: PayloadAction<Partial<BackendState>>) {
      patchStateReducer(state, action)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionUserLogout, (state) => ({ ...initialState, cardTemplates: state.cardTemplates }))
  },
})

export const {
  emailPromptShown,
  cardBlockPreparationCleared,
  cardBlockPreparingChanged,
  cardBlockPreparationSaved,
  cardUpdated,
  issueStateChanged,
  cardIssueChanged,
  patchBackendState,
} = backendSlice.actions
